import { Button } from '@/components/ui/Button';

import type { ChatGptResultSummary } from '../api';
import { describeStatus } from '../status';

export type NextStepRouteState = 'processing' | 'ready' | 'done';

type Props = {
  state: NextStepRouteState;
  status: string | null;
  latestResult: ChatGptResultSummary | null;
  onPrepareAnalysis: () => void;
  onOpenLatestResult: () => void;
};

export function resolveNextStepState(options: {
  transcriptReady: boolean;
  hasChatGptResult: boolean;
}): NextStepRouteState {
  if (!options.transcriptReady) {
    return 'processing';
  }
  if (options.hasChatGptResult) {
    return 'done';
  }
  return 'ready';
}

export function NextStepCard({
  state,
  status,
  latestResult,
  onPrepareAnalysis,
  onOpenLatestResult,
}: Props) {
  if (state === 'processing') {
    const stageLabel = describeStatus(status).label;
    return (
      <section className="next-step next-step--processing" aria-labelledby="next-step-title">
        <div className="next-step__body">
          <p className="next-step__eyebrow">Следующий шаг</p>
          <h2 className="next-step__title" id="next-step-title">
            Готовим расшифровку
          </h2>
          <p className="next-step__text">
            Дождитесь окончания распознавания — после этого можно будет подготовить анализ в
            ChatGPT.
          </p>
          <p className="next-step__meta">Текущий этап: {stageLabel}</p>
        </div>
      </section>
    );
  }

  if (state === 'ready') {
    return (
      <section className="next-step next-step--ready" aria-labelledby="next-step-title">
        <div className="next-step__body">
          <p className="next-step__eyebrow">Следующий шаг</p>
          <h2 className="next-step__title" id="next-step-title">
            Проанализируйте звонок в ChatGPT
          </h2>
          <p className="next-step__text">
            Скопируйте расшифровку вместе с заданием, откройте ChatGPT, а затем прикрепите полученный
            ответ
          </p>
          <div className="next-step__actions">
            <Button type="button" variant="primary" size="lg" onClick={onPrepareAnalysis}>
              Подготовить анализ в ChatGPT
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="next-step next-step--done" aria-labelledby="next-step-title">
      <div className="next-step__done-row">
        <span className="next-step__check" aria-hidden="true">
          ✓
        </span>
        <div className="next-step__body">
          <p className="next-step__eyebrow">Следующий шаг</p>
          <h2 className="next-step__title" id="next-step-title">
            Анализ звонка готов
          </h2>
          {latestResult !== null ? (
            <p className="next-step__meta">{latestResult.title}</p>
          ) : null}
        </div>
        <Button type="button" variant="primary" onClick={onOpenLatestResult}>
          Посмотреть результат
        </Button>
      </div>
    </section>
  );
}
